import type { CountryObject } from '@/components/helpers/metaobjects.types'

type CountryPart = {
  handle: string
  name: string
  options?: Record<string, string>
}

type BrowsingContextSuggestionsResponse = {
  detected_values?: {
    country?: CountryPart
  }
  suggestions?: Array<{
    parts?: {
      country?: CountryPart
    }
  }>
}

let suggestionsRequest: Promise<BrowsingContextSuggestionsResponse | null> | null = null

/** Shared across every `<localization-form>` on the page so the IP lookup only runs once. */
const fetchBrowsingContextSuggestions = () => {
  if (!suggestionsRequest) {
    const root = window.globals?.routes?.root || '/'
    suggestionsRequest = fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`)
      .then((response) => response.json() as Promise<BrowsingContextSuggestionsResponse>)
      .catch(() => null)
  }
  return suggestionsRequest
}

const findMatchingCountry = (handle: string): CountryObject | undefined => {
  const countries = window.globals?.metaobjects?.countries || []
  return countries.find((country) => {
    if (country.country_code?.toLowerCase() === handle.toLowerCase()) return true
    const [, countryCode] = country.hreflang?.text?.split('-') || []
    return countryCode?.toLowerCase() === handle.toLowerCase()
  })
}

const getMismatchedCountry = (response: BrowsingContextSuggestionsResponse, activeMarket: string) => {
  const detected = response.detected_values?.country
  if (detected?.handle && detected.handle.toLowerCase() !== activeMarket.toLowerCase()) {
    return detected
  }

  const suggested = response.suggestions?.[0]?.parts?.country
  if (suggested?.handle && suggested.handle.toLowerCase() !== activeMarket.toLowerCase()) {
    return suggested
  }

  return null
}

const getMarketCountryHandles = (response: BrowsingContextSuggestionsResponse): string[] => {
  const handles = new Set<string>()
  response.suggestions?.forEach((suggestion) => {
    const options = suggestion.parts?.country?.options
    if (options) Object.keys(options).forEach((code) => handles.add(code.toLowerCase()))
    const suggestedHandle = suggestion.parts?.country?.handle
    if (suggestedHandle) handles.add(suggestedHandle.toLowerCase())
  })
  return [...handles]
}

const isSwitchableMarket = (handle: string, response: BrowsingContextSuggestionsResponse): boolean => {
  const target = handle.toLowerCase()

  const marketHandles = getMarketCountryHandles(response)
  if (marketHandles.length) return marketHandles.includes(target)

  const inAvailableCountries = (window.globals?.localization?.countries || []).some(
    (country) => country.iso_code?.toLowerCase() === target
  )
  if (inAvailableCountries) return true

  return Boolean(findMatchingCountry(handle)?.hreflang?.url)
}

class LocalizationForm extends HTMLElement {
  elements: {
    input: HTMLInputElement | null
    search: HTMLInputElement | null
    list: HTMLElement | null
    modalMessage: HTMLElement | null
  }

  selectors = {
    name: 'data-name',
    value: 'data-value'
  }

  constructor() {
    super()
    this.elements = this.fetchElements()
  }

  connectedCallback() {
    this.elements = this.fetchElements()
    this.addEventListeners()
    this.detectGeoMismatch().catch(() => {})
  }

  fetchElements() {
    return {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]') as HTMLInputElement | null,
      search: this.querySelector('#CountrySearch') as HTMLInputElement | null,
      list: this.querySelector('#CountryList') as HTMLElement | null,
      modalMessage: document.querySelector('#localization-modal-message') as HTMLElement | null
    }
  }

  addEventListeners() {
    this.elements.search?.addEventListener('input', this.onSearchInput.bind(this))
    this.querySelectorAll('a[data-value]').forEach((item) =>
      item.addEventListener('click', this.onItemClick.bind(this))
    )
  }

  /**
   * Compares the visitor's IP-detected country against the active market and reveals the
   * warning message when they differ and the detected country is one we can switch to.
   */
  async detectGeoMismatch() {
    const activeMarket = window.globals?.localization?.country
    if (!this.elements.modalMessage || !activeMarket) return

    const response = await fetchBrowsingContextSuggestions()
    if (!response) return

    const mismatched = getMismatchedCountry(response, activeMarket)
    if (!mismatched?.handle || !isSwitchableMarket(mismatched.handle, response)) return

    this.elements.modalMessage.classList.remove('hidden')
  }

  onSearchInput(event: Event) {
    event.preventDefault()
    const currentTarget = event.currentTarget as HTMLInputElement
    const value = currentTarget?.value?.toLowerCase()
    this.elements?.list?.querySelectorAll('a').forEach((item) => {
      const name = item.getAttribute(this.selectors.name)?.toLowerCase()
      const itemValue = item.getAttribute(this.selectors.value)?.toLowerCase()
      const wrappingListItem = item.closest('li')
      if (!wrappingListItem) return
      if (name?.includes(value) || itemValue?.includes(value)) wrappingListItem.style.display = 'block'
      else wrappingListItem.style.display = 'none'
    })
  }

  onItemClick(event: Event) {
    event.preventDefault()
    const form = this.querySelector('form')
    const target = event.target as HTMLElement
    const dataElement = target.closest('[data-value]') as HTMLElement | null

    if (!this.elements.input || !dataElement?.dataset.value) return
    this.elements.input.value = dataElement.dataset.value
    if (form) form.submit()
  }
}

customElements.define('localization-form', LocalizationForm)
